import { useState } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Lock, Download, RefreshCcw } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [password, setPassword] = useState("");
  const [signups, setSignups] = useState([]);
  const [earlyBird, setEarlyBird] = useState([]);
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchAll = async (pwd) => {
    setLoading(true);
    try {
      const [s, e] = await Promise.all([
        axios.get(`${API}/signups`, { headers: { "X-Admin-Password": pwd } }),
        axios.get(`${API}/early-bird`, { headers: { "X-Admin-Password": pwd } }),
      ]);
      setSignups(s.data);
      setEarlyBird(e.data);
      setAuthed(true);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Fel lösenord.");
      } else {
        toast.error("Kunde inte hämta data.");
      }
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  const onLogin = (e) => {
    e.preventDefault();
    if (!password) return;
    fetchAll(password);
  };

  const exportCsv = (rows, filename, headers) => {
    const header = headers.join(",") + "\n";
    const body = rows
      .map((r) =>
        headers
          .map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`)
          .join(",")
      )
      .join("\n");
    const blob = new Blob([header + body], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportAllCsv = () => {
    const interestLabel = (v) =>
      v === "grupp" ? "Grupprogrammet" :
      v === "enskilt" ? "Enskilda samtal" :
      v === "vet_ej" ? "Vet inte ännu" : "";

    const rows = [
      ...signups.map((s) => ({
        namn: s.name || "",
        epostadress: s.email,
        valt_alternativ: "Seminariet 30 sept",
        datum_for_anmalan: s.created_at,
      })),
      ...earlyBird.map((s) => ({
        namn: s.name || "",
        epostadress: s.email,
        valt_alternativ: interestLabel(s.interest) || "Early Bird",
        datum_for_anmalan: s.created_at,
      })),
    ];
    rows.sort((a, b) => new Date(b.datum_for_anmalan) - new Date(a.datum_for_anmalan));

    const headers = ["namn", "e-postadress", "valt alternativ", "datum för anmälan"];
    const body = rows
      .map((r) =>
        [r.namn, r.epostadress, r.valt_alternativ, r.datum_for_anmalan]
          .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
          .join(",")
      )
      .join("\n");
    const csv = "\uFEFF" + headers.join(",") + "\n" + body;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `alla-anmalningar-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6] px-6">
        <form
          onSubmit={onLogin}
          className="w-full max-w-md bg-white border border-[#DCD5CE] rounded-3xl p-10"
          data-testid="admin-login-form"
        >
          <div className="h-12 w-12 rounded-full bg-[#F3EFEA] flex items-center justify-center mb-6">
            <Lock className="h-5 w-5 text-[#A98C78]" />
          </div>
          <h1 className="font-serif-display text-3xl text-[#2C2825]">Admin</h1>
          <p className="text-sm text-[#5C5651] mt-2">Logga in för att se anmälningar.</p>
          <Input
            type="password"
            placeholder="Lösenord"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password"
            className="h-12 rounded-full bg-[#FAF9F6] border-[#DCD5CE] mt-7 px-5"
          />
          <Button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="w-full h-12 rounded-full bg-[#A98C78] hover:bg-[#8C7362] text-white tracking-[0.2em] uppercase text-xs mt-4"
          >
            {loading ? "Loggar in..." : "Logga in"}
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6] px-6 py-12" data-testid="admin-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-[#8C7362]">Admin</p>
            <h1 className="font-serif-display text-4xl text-[#2C2825] mt-2">Anmälningar</h1>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => fetchAll(password)}
              variant="outline"
              data-testid="admin-refresh"
              className="rounded-full border-[#DCD5CE] text-[#5C5651] hover:bg-[#F3EFEA]"
            >
              <RefreshCcw className="h-4 w-4 mr-2" /> Uppdatera
            </Button>
            <Button
              onClick={exportAllCsv}
              data-testid="admin-export-all"
              disabled={signups.length + earlyBird.length === 0}
              className="rounded-full bg-[#A98C78] hover:bg-[#8C7362] text-white disabled:opacity-60"
            >
              <Download className="h-4 w-4 mr-2" />
              Exportera alla anmälningar
            </Button>
          </div>
        </div>

        <Tabs defaultValue="webinar" className="w-full">
          <TabsList className="bg-[#F3EFEA] border border-[#E6DFD7] p-1 rounded-full h-auto mb-6">
            <TabsTrigger
              value="webinar"
              className="rounded-full px-6 py-2 text-sm data-[state=active]:bg-white data-[state=active]:text-[#2C2825]"
              data-testid="admin-tab-webinar"
            >
              Seminariet ({signups.length})
            </TabsTrigger>
            <TabsTrigger
              value="earlybird"
              className="rounded-full px-6 py-2 text-sm data-[state=active]:bg-white data-[state=active]:text-[#2C2825]"
              data-testid="admin-tab-earlybird"
            >
              Early Bird ({earlyBird.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="webinar">
            <div className="flex justify-end mb-4">
              <Button
                onClick={() =>
                  exportCsv(signups, "anmalningar-seminariet", ["name", "email", "created_at"])
                }
                data-testid="export-webinar"
                className="rounded-full bg-[#2C2825] hover:bg-[#3D3833] text-white"
              >
                <Download className="h-4 w-4 mr-2" /> Exportera CSV
              </Button>
            </div>
            <div className="bg-white border border-[#E6DFD7] rounded-3xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#F3EFEA] text-xs uppercase tracking-[0.2em] text-[#8C7362]">
                  <tr>
                    <th className="text-left px-6 py-4">E-post</th>
                    <th className="text-left px-6 py-4">Anmäld</th>
                  </tr>
                </thead>
                <tbody>
                  {signups.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="text-center py-12 text-[#8C7362]">
                        Inga anmälningar än.
                      </td>
                    </tr>
                  ) : (
                    signups.map((s) => (
                      <tr key={s.id} className="border-t border-[#E6DFD7]">
                        <td className="px-6 py-4 text-[#2C2825]">{s.email}</td>
                        <td className="px-6 py-4 text-[#8C7362] text-sm">
                          {new Date(s.created_at).toLocaleString("sv-SE")}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="earlybird">
            <div className="flex justify-end mb-4">
              <Button
                onClick={() =>
                  exportCsv(
                    earlyBird,
                    "early-bird-intresse",
                    ["name", "email", "phone", "interest", "message", "created_at"]
                  )
                }
                data-testid="export-earlybird"
                className="rounded-full bg-[#2C2825] hover:bg-[#3D3833] text-white"
              >
                <Download className="h-4 w-4 mr-2" /> Exportera CSV
              </Button>
            </div>
            <div className="bg-white border border-[#E6DFD7] rounded-3xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#F3EFEA] text-xs uppercase tracking-[0.2em] text-[#8C7362]">
                  <tr>
                    <th className="text-left px-6 py-4">Namn</th>
                    <th className="text-left px-6 py-4">E-post</th>
                    <th className="text-left px-6 py-4">Telefon</th>
                    <th className="text-left px-6 py-4">Intresse</th>
                    <th className="text-left px-6 py-4">Meddelande</th>
                    <th className="text-left px-6 py-4">Skickat</th>
                  </tr>
                </thead>
                <tbody>
                  {earlyBird.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-12 text-[#8C7362]">
                        Inga intresseanmälningar än.
                      </td>
                    </tr>
                  ) : (
                    earlyBird.map((s) => (
                      <tr key={s.id} className="border-t border-[#E6DFD7] align-top">
                        <td className="px-6 py-4 text-[#2C2825]">{s.name}</td>
                        <td className="px-6 py-4 text-[#5C5651]">{s.email}</td>
                        <td className="px-6 py-4 text-[#5C5651]">{s.phone || "—"}</td>
                        <td className="px-6 py-4 text-[#5C5651]">
                          {s.interest === "grupp"
                            ? "Grupp"
                            : s.interest === "enskilt"
                            ? "Enskilt"
                            : s.interest === "vet_ej"
                            ? "Vet ej"
                            : "—"}
                        </td>
                        <td className="px-6 py-4 text-[#5C5651] text-sm max-w-xs">
                          {s.message || "—"}
                        </td>
                        <td className="px-6 py-4 text-[#8C7362] text-sm whitespace-nowrap">
                          {new Date(s.created_at).toLocaleString("sv-SE")}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
